import style from "./css/SettingPreview.module.css";
import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { Button, Checkbox, Form, Input, Select } from "antd";
import { Api } from "../../uikit/service/api";
import func from "@uikit/func";
import { editorActions } from "../../store/editor";
import LoadingOutline from "../components/LoadingOutline";
import { getQueryParams } from "./util";
import { motion, AnimatePresence } from "framer-motion";
import { logEvent } from "@uikit/service/amplitude";
import { get } from "lodash";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";

const slideVariants = {
  init: {
    opacity: 0,
    x: "100%",
  },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      // type: 'spring',
      // ease: "easeInOut",
    },
  },
  hide: {
    opacity: 0,
    x: "100%",
    transition: {
      duration: 0.3,
    },
  },
};

const priceList = [
  {
    title: "Free",
    content: "Your users will not be charged for using your app",
  },
  {
    title: "Subscription",
    content: "Charge a monthly fee for your service.",
  },
];
const { Option } = Select;
const linkDescription =
  "Imagica uses Stripe to provide payment services for our creators, please activate your Stripe Connect account using the link below.";

function SettingPreview(props) {
  const api = new Api(false);
  const dispatch = useDispatch();
  const subscriptionRef = useRef(null);
  const store = useStore();

  const urlParams = getQueryParams();
  const [stripeAccountId, setStripeAccountId] = useState("");

  const selectedTemplate = useSelector(state => state.studio.selectedTemplate);

  const settingPreviewData = useSelector(state => state.editor.settingPreviewData);
  const setSettingPreviewData = val => {
    dispatch(editorActions.setSettingPreviewData(val));
  };

  const clickOption = title => {
    setSettingPreviewData({ currentPriceOption: title });
    if (title === "Subscription") {
      handleEmpowerInfo();
      getSubscriptionDetail();
    }
    const eventProperties = {
      option: title,
    };
    logEvent("click_publish_option", eventProperties);
  };

  const handleEmpowerInfo = async () => {
    let accountStandard = {};
    try {
      setSettingPreviewData({ subscriptionLoading: true });
      // 获取当前用户支付帐号信息
      accountStandard = await api.getWithError("/be/payment/stripe/account/express");
      setStripeAccountId(accountStandard?.data?.data?.id || "");
      // const resAccountStandard = await accountStandard.json()
    } catch (error) {
      console.error("error::::", error);
    }
    try {
      // 不存在则创建账号并生成stripe激活链接
      if (func.isEmpty(accountStandard?.data?.data?.id)) {
        const data = {
          capabilities: {
            card_payments: { requested: true },
          },
        };
        const accountExpress = await api.postWithError("/be/payment/stripe/account/type/express", data);
        if (func.isEmpty(accountExpress?.data?.accountId)) {
          throw "Failed to create account.";
        }
        setStripeAccountId(accountExpress.data.accountId);
        setStripeLink();
        return;
      }
      // 明天需要沟通激活链接过期或者出现错误payouts_enabled是否为false
      if (urlParams.type === "stripeConnectExpired") {
        setStripeLink();
        return;
      }
      // 存在支付帐号 payouts_enabled(激活)
      accountStandard?.data?.data?.payouts_enabled
        ? setSettingPreviewData({ isEmpower: true, subscriptionLoading: false })
        : setStripeLink();
    } catch (error) {
      console.error("error::::", error);
      func.messageUtil(error || "Request failed.", "error");
    }
  };
  // 生成stripe激活链接
  const setStripeLink = async () => {
    try {
      // const updatedProject = await props.saveAsExistingTemplateV2();
      const { updated_project: updatedProject } = await props.handleSave();
      const redirectUrl = `${window.location.origin}${window.location.pathname}#/editor/${updatedProject.index}?selectedUI=saasV2&type=stripeConnectExpired&previewSetting=true&stripeAccountActivated=false`;
      const parmasData = {
        return_url: `${window.location.origin}${window.location.pathname}#/editor/${updatedProject.index}?selectedUI=saasV2&previewSetting=true&stripeAccountActivated=true`,
        refresh_url: `${window.location.origin}${
          window.location.pathname
        }#/editor/error?redirect_url=${encodeURIComponent(redirectUrl)}`,
        type: "account_onboarding",
      };
      // stripe激活链接
      const accountLink = await api.postWithError("/be/payment/stripe/account/link", parmasData);
      if (func.isEmpty(accountLink?.url)) throw "Get link failed.";
      setSettingPreviewData({ subscriptionLoading: false, otherLink: accountLink.data.url });
    } catch (error) {
      console.error("error::::", error);
      func.messageUtil(error || "Request failed.", "error");
    }
  };

  const centToDollar = cent => {
    if (isNaN(Number(cent))) return "";
    return +(cent / 100).toFixed(2);
  };

  const getSubscriptionDetail = async () => {
    const id = selectedTemplate?.published_metadata?.stripe_product_id || "";
    if (func.isEmpty(id)) return;
    const productRes = await api.getWithError(`/be/payment/subscription/product/${id}`);
    const priceRes = await api.getWithError(`/be/payment/subscription/price?product=${id}`);
    const name = get(productRes, "data.name", "");
    const description = get(productRes, "data.description", "");
    const subscriptionPeriod = get(priceRes, "data.data.0.recurring.interval_count", "");
    const interval = get(priceRes, "data.data.0.recurring.interval", "");
    const unitAmount = get(priceRes, "data.data.0.unit_amount", "");
    const newObj = {
      name,
      description,
      subscriptionPeriod,
      interval,
      price: centToDollar(unitAmount) || "",
    };
    setSettingPreviewData(newObj);
    subscriptionRef.current.setFieldsValue(newObj);
  };

  const onFinish = async values => {
    try {
      props.setSettingPreviewLLoading(true);
      // 获取 product id
      const productData = {
        name: values.name,
        description: values.description,
        metadata: {
          type: "studios",
          uuid: selectedTemplate.index,
        },
      };
      const resProduct = await api.postWithError("/be/payment/subscription/product/", productData);
      if (func.isEmpty(resProduct?.data?.id)) {
        throw "Request subscription product failed.";
      }

      // 获取price id
      const priceData = {
        currency: "usd",
        unit_amount: values.price * 100,
        product: resProduct.data.id,
        recurring: {
          interval: settingPreviewData.interval,
          interval_count: values.subscriptionPeriod,
        },
        metadata: {
          type: "studios",
          uuid: selectedTemplate.index,
        },
      };
      const resPrice = await api.postWithError("/be/payment/subscription/price", priceData);
      if (func.isEmpty(resPrice?.data?.id)) {
        throw "Request subscription price failed.";
      }

      const ids = {
        stripe_product_id: resProduct.data.id,
        stripe_price_id: resPrice.data.id,
        stripe_account_id: stripeAccountId,
      };
      // const ids = {
      //   stripe_product_id: "prod_Nkjr8YFdsrb2DG",
      //   stripe_price_id: "price_1MzEcNGDNVcH7jzYaV8VyYyL"
      // }
      props.handleSubscriptionAndPublish(ids);
    } catch (error) {
      console.error("error::::", error);
      props.setSettingPreviewLLoading(false);
      func.messageUtil(error || "Request failed.", "error");
    }
  };

  useEffect(() => {
    if (
      settingPreviewData.currentPriceOption === "Subscription" &&
      settingPreviewData.isEmpower &&
      subscriptionRef.current
    ) {
      subscriptionRef.current.setFieldsValue(settingPreviewData);
    }
  }, [props.showSettingPreview, settingPreviewData.currentPriceOption, settingPreviewData.isEmpower, subscriptionRef]);

  useEffect(() => {
    if (urlParams.previewSetting === "true") {
      if (urlParams.stripeAccountActivated === "true") {
        setSettingPreviewData({ currentPriceOption: "Subscription" });
        handleEmpowerInfo();
        return;
      }
      if (urlParams.stripeAccountActivated === "false" && urlParams.type === "stripeConnectExpired") {
        setSettingPreviewData({ currentPriceOption: "Subscription" });
        handleEmpowerInfo();
      }
    }
  }, []);

  const selectAfter = (
    <Select
      defaultValue="Weeks"
      value={settingPreviewData.interval}
      onChange={e => setSettingPreviewData({ interval: e })}
    >
      <Option value="week">Weeks</Option>
      <Option value="month">Months</Option>
      <Option value="year">Years</Option>
    </Select>
  );

  const clickOK = () => {
    const ids = {
      stripe_product_id: "",
      stripe_price_id: "",
    };
    props.setSettingPreviewLLoading(true);
    props.handleSubscriptionAndPublish(ids);
    logEvent("click_publish_setting_ok");
  };

  const clickToLink = () => {
    window.open(settingPreviewData.otherLink, "_blank");
    logEvent("click_stripe_link");
  };

  return (
    <AnimatePresence>
      {props.showSettingPreview ? (
        <motion.div
          className={style["preview-setting-page"]}
          variants={slideVariants}
          initial="init"
          animate="show"
          exit="hide"
        >
          <div
            className={style["setting-header"]}
            onClick={() => {
              logEvent("exit_publish_settings");
              props.setShowSettingPreview(false);
            }}
          >
            {/* <span
                className={`iconfont icon-shanchu1 ${style['icon-del']}`}
                onClick={() => props.setShowSettingPreview(false)}
              ></span> */}
            <img src={`${settings.S3CDN}${settings.viewAssetsPath}setting-back.png`} />
            <span>Publish Settings</span>
          </div>

          <div className={style["pricing-options"]}>
            <p>Pricing Options</p>
            <div className={style["price-box"]}>
              {priceList.map(x => {
                return (
                  <div
                    key={x.title}
                    className={`${x.title === settingPreviewData.currentPriceOption ? style["select-price"] : ""}`}
                    onClick={() => clickOption(x.title)}
                  >
                    <h3>{x.title}</h3>
                    <label>{x.content}</label>
                  </div>
                );
              })}
            </div>
            {settingPreviewData.currentPriceOption === "Subscription" ? (
              settingPreviewData.subscriptionLoading ? (
                <div className={style["subscription-loading"]}>
                  <LoadingOutline />
                </div>
              ) : settingPreviewData.isEmpower ? (
                <Form
                  ref={subscriptionRef}
                  name="basic"
                  layout="vertical"
                  onFinish={onFinish}
                  className={style["form-box"]}
                >
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please input your name!",
                      },
                    ]}
                  >
                    <Input
                      value={settingPreviewData.name}
                      onChange={e => setSettingPreviewData({ name: e.target.value })}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Description"
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: "Please input your description!",
                      },
                    ]}
                  >
                    <Input
                      value={settingPreviewData.description}
                      onChange={e => setSettingPreviewData({ description: e.target.value })}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Subscription Renewal Period"
                    name="subscriptionPeriod"
                    className={style["subscription-item"]}
                    rules={[
                      {
                        required: true,
                        message: "Please input your subscription period!",
                      },
                    ]}
                  >
                    <Input
                      addonAfter={selectAfter}
                      value={settingPreviewData.subscriptionPeriod}
                      onChange={e => setSettingPreviewData({ subscriptionPeriod: e.target.value })}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Price Per Period"
                    name="price"
                    className={style["price-item"]}
                    rules={[
                      {
                        required: true,
                        message: "Please input your price!",
                      },
                    ]}
                  >
                    <Input
                      addonBefore="$"
                      value={settingPreviewData.price}
                      onChange={e => setSettingPreviewData({ price: e.target.value })}
                    />
                  </Form.Item>

                  <Form.Item className={style["form-ok-btn"]}>
                    <Button type="primary" htmlType="submit" loading={props.settingPreviewLoading}>
                      OK
                    </Button>
                  </Form.Item>
                </Form>
              ) : (
                <div className={style["link-description"]}>
                  <label>{linkDescription}</label>
                  <span onClick={clickToLink}>{settingPreviewData.otherLink}</span>
                </div>
              )
            ) : (
              <div className={style["form-ok"]}>
                <Button type="primary" htmlType="submit" onClick={clickOK} loading={props.settingPreviewLoading}>
                  OK
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default SettingPreview;
